import { useMemo, useCallback } from "react"
import { useOutletContext } from "react-router-dom"
import PropTypes from "prop-types"
import { map, chain, get, isEmpty, orderBy, some, first, pick, isNil, noop } from "lodash"
import { Accordion, AccordionDetails, AccordionSummary, IconButton } from "@mui/material"
import { Add, ExpandLess, ExpandMore, Remove, InfoOutlined } from "@mui/icons-material"
import classNames from "classnames"
import OPTooltip from "components/material/tooltip"
import { handleGenerateHeaders, handleGenerateRows } from "connected-components/dashboard/components/download/helpers"
import { ERROR, LOADING, OK } from "helpers/data-statuses"
import { useDownloadSubscribers } from "hooks/register-downloads"
import singleDateContract from "gatherers/single-date/contract"
import useDownloadData from "../../hooks/download-data"
import CompareSectionStatus from "./loading"
import SkeletonSection from "../skeleton-section"

import "./style.scss"

const MAX_ROWS = 8

export default function CompareSection(props) {
  const { setDownloadData = noop } = useOutletContext()
  const {
    status,
    data,
    name,
    isGroup,
    formatter,
    configKey,
    color,
    defaultExpanded = false,
    sorted = false,
    columnCount,
    rowCount = MAX_ROWS,
    instrumentsWithMetadata,
    positionSetsMetadata,
    downloadName,
    index: sectionIndex,
    alwaysMultipleRows = false,
    queryState = { expanded: defaultExpanded, all: false },
    onQueryStateChange = noop,
    isEndVsStartDateComparison,
    useSkeletonLoader = false,
    buttonSize = "medium",
  } = props

  const rows = useMemo(() => chain(data)
    .reduce((accumulator, item, columnIndex) => {
      for (const id in item.values) {
        if (!accumulator[id]) {
          accumulator[id] = {
            id,
            ...pick(item.values[id], ["label", "formatter", "tooltip"]),
            values: Array(data.length).fill(null),
          }
        }
        accumulator[id].values[columnIndex] = item.values[id].value
      }

      return accumulator
    }, {})
    .map()
    .thru(rows => {
      if (!sorted) {
        return rows
      }

      return orderBy(rows, ({ values }) => {
        for (const value of values) {
          if (value) {
            return Math.abs(value)
          }
        }
      }, "desc")
    })
    .value(),
  [data, sorted])

  const generateHeaders = useCallback(() =>
    handleGenerateHeaders(instrumentsWithMetadata, positionSetsMetadata, "Metric", isEndVsStartDateComparison),
  [instrumentsWithMetadata, positionSetsMetadata, isEndVsStartDateComparison])

  const generateRows = useCallback(() =>
    handleGenerateRows(rows, formatter),
  [rows, formatter])

  const generatedDownloadName = useMemo(() => downloadName || `${name}${isGroup ? " Group" : ""}`, [name, isGroup, downloadName])

  useDownloadData({
    configKey,
    sectionIndex,
    name: generatedDownloadName,
    setDownloadData,
    generateHeaders,
    generateRows,
  })

  // Dashboard KPI downloads should be combined into a single sheet
  useDownloadSubscribers(`kpi.${configKey}`, () => ({
    combineSubscribers: true,
    name: "KPI",
    data: { rows: [[name], ...generateRows()], headers: generateHeaders() },
  }))

  const summary = useMemo(() => map(data, item => get(item, "summary")), [data])
  const nameStyle = color ? { color } : null

  if (status === LOADING) {
    if (useSkeletonLoader) {
      return <SkeletonSection rowCount={rowCount} colCount={columnCount} />
    }
    return <CompareSectionStatus {...{
      status: "Loading...",
      expanded: queryState?.expanded,
      color,
      name,
      nameStyle,
      columnCount,
      rowCount,
    }}
    />
  }

  if (status === ERROR) {
    return <div>Error</div>
  }

  if (isEmpty(rows) && some(summary, value => !!value)) {
    return <Accordion square className="renderers_compare-section single-row">
      <AccordionSummary className="header">
        <div style={{ backgroundColor: color }} />
        <div className="name" style={nameStyle}>
          <span>{name}</span>
        </div>
        {map(summary, (c, i) => <div className="single-row-summary-value" key={`summary-value-${i}`}>{formatter(c)}</div>)}
      </AccordionSummary>
    </Accordion>
  }

  if (isEmpty(rows)) {
    return null
  }

  const showAllButton = rows.length > MAX_ROWS

  let adjustedName = name
  if (rows.length === 1 && !alwaysMultipleRows) {
    if (name !== first(rows).label) {
      adjustedName = `${name} (${first(rows).label})`
    }

    return <Accordion
      square
      className="renderers_compare-section single-row"
      expanded={false}
    >
      <AccordionSummary className="header" data-test-class="compare-section-summary">
        <div style={{ backgroundColor: color }} />
        <div className="name" style={nameStyle}>{adjustedName}</div>
        {map(first(rows).values, (value, index) => <div key={index}>{formatter(value)}</div>)}
      </AccordionSummary>
    </Accordion>
  }

  return <Accordion
    square
    className={classNames("renderers_compare-section", { all: queryState?.all })}
    expanded={queryState?.expanded}
    onChange={(_, expanded) => { onQueryStateChange({ expanded }) }}
    data-test-class="compare-section-summary"
  >
    <AccordionSummary className="header" component="div">
      <div style={{ backgroundColor: color }} />
      <div className="name" data-test-class="compare-section-label" style={nameStyle}>
        <span>{adjustedName}</span>
        <IconButton size={buttonSize} className="expand-icon">
          {queryState?.expanded ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
        </IconButton>
        {showAllButton && queryState?.expanded && <IconButton
          size={buttonSize}
          className="show-all-icon"
          onClick={e => {
            e.stopPropagation()
            onQueryStateChange({ all: !queryState?.all })
          }}
        >
          {queryState?.all ? <Remove fontSize="inherit" /> : <Add fontSize="inherit" />}
        </IconButton>}
      </div>
      {map(summary, (c, i) => <div className="summary-value" key={i}><span>{formatter(c)}</span></div>)}
    </AccordionSummary>
    <AccordionDetails className="body">
      {map(rows, (d, id) => {
        const tooltip = d.tooltip ? <OPTooltip title={d.tooltip}><InfoOutlined fontSize="small" /></OPTooltip> : null
        return <div key={id} data-test-class="compare-section-row">
          <div style={{ backgroundColor: color }} />
          <div className="tooltip-label-wrapper">
            <span className="tooltip-label">{d.label}</span>
            <span>{tooltip}</span>
          </div>
          {map(d.values, (value, index) => {
            const formattedValue = isNil(value) ? "-" : (d.formatter ?? formatter)(value)
            return <div data-test-class="compare-section-row-value" key={index}>{formattedValue}</div>
          })}
        </div>
      },
      )}
    </AccordionDetails>
  </Accordion>
}

CompareSection.propTypes = {
  configKey: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  status: PropTypes.oneOf([OK, ERROR, LOADING]).isRequired,
  data: PropTypes.arrayOf(singleDateContract),
  formatter: PropTypes.func.isRequired,
  color: PropTypes.string,
  defaultExpanded: PropTypes.bool,
  sorted: PropTypes.bool,
  columnCount: PropTypes.number.isRequired,
  rowCount: PropTypes.number,
  isGroup: PropTypes.bool,
  positionSetsMetadata: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  instrumentsWithMetadata: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  })).isRequired,
  downloadName: PropTypes.string,
  index: PropTypes.number.isRequired,
  alwaysMultipleRows: PropTypes.bool,
  queryState: PropTypes.shape({
    expanded: PropTypes.bool,
    all: PropTypes.bool,
  }),
  onQueryStateChange: PropTypes.func,
  isEndVsStartDateComparison: PropTypes.bool,
  useSkeletonLoader: PropTypes.bool,
  buttonSize: PropTypes.string,
}
